import { P2GBox, P2GButton, P2GCheckBoxLegacy, P2GGrid, P2GLink, P2GLinkGroup, P2GText } from '@parcel2go/core-ui';
import { Dimensions, PackageApiModel } from '~/lib/api/smartsend/types';
import { DimensionsSelection } from '../DimensionsSelection';
import { GroupingParcelProps } from './GroupingParcelProps';
import { AppCard } from '~/components/card/app-card';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { ProductContentFields } from '../ProductContentFields';
import { LocalUnitsEntry } from '~/apps/smartsend/lib/helpers/DimensionUnits';

interface props extends GroupingParcelProps {
  // The local-units entry is part of the update, not an extra: DimensionsSelection hands back what the
  // user actually typed alongside the metric value, and dropping it means an imperial entry survives
  // only until this component unmounts.
  onParcelDimensionsUpdate?: (parcelUID: string, dims: Dimensions, localUnitsEntry?: LocalUnitsEntry) => void;
  hideDimensionsIfNotRequired?: boolean;
  getPackages?: () => Promise<PackageApiModel[]>;
  children?: React.ReactNode;
  currencyCode: string;
}

export const GroupingPanelParcels: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const liftParcelLabel = props.parcels.some((p) => p.Contents.length > 1);

  return (
    <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {props.parcels.map((p, pi) => {
        const showContents = p.Contents.some((o) => props.selected?.includes(o.UID));
        return (
          <P2GBox key={p.UID}>
            {liftParcelLabel && (
              <P2GBox mb={3}>
                <P2GText variant="subtitle2">
                  {t('BOX_CONTENTS_TITLE', {
                    defaultValue: 'Box {{parcelNumber}} of {{parcelCount}}',
                    parcelNumber: pi + 1,
                    parcelCount: props.parcels.length
                  })}
                </P2GText>
              </P2GBox>
            )}
            <AppCard>
              <P2GBox mt={pi > 0 ? 2 : 0} p={2} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {p.Contents.map((o, oi) => (
                  <P2GBox key={o.UID}>
                    <P2GGrid container wrap="nowrap" alignContent="center" alignItems="center" spacing={1}>
                      {props.onSelect && (
                        <P2GGrid item flexShrink={1}>
                          <P2GCheckBoxLegacy
                            onChange={() => props.onSelect?.(o.UID)}
                            checked={props.selected?.includes(o.UID)}
                            labelPlacement="end"
                            variant="primary"
                            error={false}
                            label={null}
                            id="parcel"
                          />
                        </P2GGrid>
                      )}

                      <P2GGrid item flexGrow={1}>
                        <P2GBox my={1}>
                          <P2GText>
                            <strong>
                              {liftParcelLabel
                                ? p.Contents.length === 1
                                  ? t('CONTENTS_SINGLE_NUMBER_TITLE', {
                                      defaultValue: 'Contents:'
                                    })
                                  : t('CONTENTS_NUMBER_TITLE', {
                                      defaultValue: 'Contents {{contentNumber}}:',
                                      contentNumber: oi + 1
                                    })
                                : t('BOX_CONTENTS_HEADING', {
                                    defaultValue: 'Box {{parcelNumber}} of {{parcelCount}}:',
                                    parcelNumber: pi + 1,
                                    parcelCount: props.parcels.length
                                  })}
                            </strong>
                          </P2GText>
                        </P2GBox>
                      </P2GGrid>
                      <P2GGrid item flexShrink={1}>
                        <P2GLinkGroup
                          links={[
                            props.allowExpanding && o.Quantity > 1 && o.Quantity <= 10 && (
                              <P2GLink onClick={() => props.expand?.(o)} variant="body2">
                                {t('EXPAND_QUANTITY_LINK', { defaultValue: 'Expand Contents', qty: o.Quantity })}
                              </P2GLink>
                            ),
                            // Only a row the customer typed themselves, which is exactly what Manual
                            // marks - addManualContent is the only thing that sets it.
                            //
                            // NOT `o.Id == null`, which was the first attempt: Id is the MARKETPLACE
                            // order-line id, and a CSV import has none at all (the mapper writes
                            // MarketplaceOrderId, never Id), so every row of an imported CSV order
                            // read as manual and stayed removable. Removing an imported row drops it
                            // silently - GenerateCustomSplitItems builds each new item purely from
                            // Contents, and planOrderLineSync returns early for a line with no rows.
                            props.removeContentItem && o.Manual && p.Contents.length > 1 && (
                              <P2GLink onClick={() => props.removeContentItem?.(p.UID, o.UID)} variant="body2">
                                {t('REMOVE_CONTENT_ITEM_LINK', { defaultValue: 'Remove' })}
                              </P2GLink>
                            )
                          ]}
                        />
                      </P2GGrid>
                    </P2GGrid>
                    <ProductContentFields
                      description={o.Description}
                      descriptionLabel={
                        o.Manual
                          ? t('MANUAL_ITEM_INPUT_LABEL', { defaultValue: 'Manual Item' })
                          : t('ITEM_DESCRIPTION_INPUT_LABEL', { defaultValue: 'Item Description' })
                      }
                      onDescriptionChange={
                        props.updateDescription ? (v) => props.updateDescription(o.UID, v) : undefined
                      }
                      sku={o.Sku}
                      skuMode="editable"
                      onSkuChange={props.updateSku ? (v) => props.updateSku(o.UID, v) : undefined}
                      quantity={o.Quantity}
                      onQuantityChange={props.updateQuantity ? (v) => props.updateQuantity(o.UID, v) : undefined}
                      quantityMode={props.updateQuantity ? 'text' : 'readonly'}
                      value={o.Value}
                      onValueChange={props.updateValue ? (v) => props.updateValue(o.UID, v) : undefined}
                      currencyCode={props.currencyCode}
                      valueMode={props.updateValue ? 'editable' : 'readonly'}
                      countryOfManufacture={o.CountryOfManufacture}
                      onCountryOfManufactureChange={
                        props.updateCountryOfManufacture ? (v) => props.updateCountryOfManufacture(o.UID, v) : undefined
                      }
                      countries={props.countries}
                      tariffCode={o.TariffCode}
                      onTariffCodeChange={props.updateTariffCode ? (v) => props.updateTariffCode(o.UID, v) : undefined}
                      tariffSystem={props.tariffSystem}
                      productValidation={props.productValidation}
                      sender={props.sender}
                      recipient={props.recipient}
                    />
                  </P2GBox>
                ))}
                {showContents && <P2GBox my={1}>{props.children}</P2GBox>}
                {props.addContentItem && (
                  <P2GBox>
                    <P2GButton onClick={() => props.addContentItem?.(p.UID)}>
                      {t('ADD_CONTENT_ITEM_LINK', { defaultValue: 'Add another item' })}
                    </P2GButton>
                  </P2GBox>
                )}
                {props.allowInlineDimensions && (
                  <P2GBox>
                    <DimensionsSelection
                      startMinimised={false}
                      getPackages={props.getPackages}
                      dimensions={p.Dimensions}
                      localUnitsEntry={p.LocalUnits}
                      onUpdate={(d, localUnitsEntry) => props.onParcelDimensionsUpdate?.(p.UID, d, localUnitsEntry)}
                      dimensionsRequired={props.dimensionsRequired}
                      hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
                    />
                  </P2GBox>
                )}
              </P2GBox>
            </AppCard>
          </P2GBox>
        );
      })}
    </P2GBox>
  );
};
